import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './useAuth'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/**
 * Route guard that redirects unauthenticated users to /login.
 *
 * Preserves the intended destination in location state so the user
 * is redirected back after successful login:
 *   navigate('/login', { state: { from: location } })
 *   → after login: navigate(state.from ?? '/dashboard', { replace: true })
 *
 * Shows nothing (null) while the session restoration check is in progress
 * to prevent a flash of the login page for authenticated users on refresh.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Session restoration in progress — don't render anything yet
  // This prevents the "flash to login" UX on page refresh
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--color-bg)',
      }}>
        <div style={{
          width: 32,
          height: 32,
          border: '2px solid var(--color-border)',
          borderTopColor: 'var(--color-accent)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
